import { useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useHref, useLocation, useMatches } from 'react-router';

import { ErrorBoundary } from '@sorare/error-boundary';

import type { RouteMetadata } from '#src/contexts/seo/index.tsx';
import { useSeoContext } from '#src/contexts/seo/index.tsx';
import { useSportContext } from '#src/contexts/sport/index.ts';
import { useQuery } from '#src/hooks/graphql/useQuery.ts';
import { toLanguageCode } from '#src/lib/i18n.ts';
import {
  buildMetaValues,
  buildMetaVariables,
  formatWhenComplete,
  resolveMetaImage,
} from '#src/lib/seo/meta/resolve.ts';
import type {
  RouteMeta as Declaration,
  RouteMetaDocument,
} from '#src/lib/seo/meta/types.ts';

type Params = Record<string, string | undefined>;

const routeMetaOf = (handle: unknown) =>
  handle && typeof handle === 'object' && 'meta' in handle
    ? (handle as { meta?: Declaration }).meta
    : undefined;

type AppliedProps = {
  meta: Declaration;
  params: Params;
  data: unknown;
};

const AppliedRouteMeta = ({ meta, params, data }: AppliedProps) => {
  const { formatMessage } = useIntl();
  const { setRouteMetadata } = useSeoContext();
  const { pathname } = useLocation();
  const href = useHref(pathname);

  const values = buildMetaValues({ meta, params, data, formatMessage });
  const title = formatWhenComplete(meta.title, values, formatMessage);
  const description = formatWhenComplete(
    meta.description,
    values,
    formatMessage
  );
  const img = resolveMetaImage(meta.image, data, href);

  const applied = useRef<RouteMetadata>({});

  useEffect(() => {
    applied.current = {
      title: title ?? applied.current.title,
      description: description ?? applied.current.description,
      img: img ?? applied.current.img,
    };
    return setRouteMetadata(applied.current);
  }, [title, description, img, setRouteMetadata]);

  return null;
};

type QueryProps = {
  meta: Declaration;
  query: RouteMetaDocument;
  params: Params;
};

const RouteMetaFromQuery = ({ meta, query, params }: QueryProps) => {
  const { sport } = useSportContext();
  const { locale } = useIntl();

  const variables = useMemo(
    () =>
      buildMetaVariables(meta.variables, {
        params,
        sport,
        lang: toLanguageCode(locale),
      }),
    [meta.variables, params, sport, locale]
  );

  const { data } = useQuery(query, {
    variables,
    // `cache` routes ride on the page's own request: the declared document is
    // read out of the cache the page query fills, never sent.
    fetchPolicy: meta.source === 'network' ? 'cache-and-network' : 'cache-only',
  });

  return <AppliedRouteMeta meta={meta} params={params} data={data} />;
};

/**
 * Applies the `meta.ts` of the deepest matched route. It runs off the route
 * match, so the page component never has to render and its own query never has
 * to resolve for the title to settle.
 */
export const RouteMeta = () => {
  const matches = useMatches();

  const matched = [...matches]
    .reverse()
    .map(match => ({
      // Keyed by the matched url, not the route id: two urls of the same route
      // describe two different things, and held values must not carry over.
      key: match.pathname,
      params: match.params,
      meta: routeMetaOf(match.handle),
    }))
    .find(({ meta }) => !!meta);

  if (!matched?.meta) {
    return null;
  }

  const { key, params, meta } = matched;

  if (meta.query) {
    // `useQuery` throws a failed request, and this sits above every route:
    // metadata that cannot load keeps the defaults instead of taking the app down.
    return (
      <ErrorBoundary key={key} fallback={null}>
        <RouteMetaFromQuery meta={meta} query={meta.query} params={params} />
      </ErrorBoundary>
    );
  }

  return (
    <AppliedRouteMeta key={key} meta={meta} params={params} data={undefined} />
  );
};
